import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  useMediaQuery,
  Button,
  TablePagination,
  CircularProgress,
  alpha,
} from "@mui/material";
import { Add, ArrowForwardRounded, CheckRounded } from "@mui/icons-material";
import { CompanyResponse } from "../../Models/company";
import { useNavigate } from "react-router-dom";
import theme from "../../Constants/theme";
import colors from "../../Constants/colors";
import { useTranslation } from "react-i18next";
import { useCompanies } from "../../Query/useCompanies";
import { useTerminology } from "../../Core/useTerminology";
import { getEntityIconByName } from "../../Helpers/getEntityIcon";
import { entityIcons } from "../../Constants/entityIcons";

interface SelectCompanyProps {
  selectedCompanyId: number | "";
  handleSelectChange: (companyId: number) => void;
  isEvaluateLoading: boolean;
  companyResponse?: CompanyResponse;
  onRefetchCompanies?: () => void;
  onCompanySelected?: () => void;
}

const SelectCompany: React.FC<SelectCompanyProps> = ({
  selectedCompanyId,
  handleSelectChange,
  onCompanySelected,
}) => {
  const { t } = useTranslation("evaluationPage");
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const terminology = useTerminology();

  const [page, setPage] = useState(1);
  const pageSize = 10;
  const sortField: keyof CompanyResponse["companies"][0] = "name";
  const sortOrder: "asc" | "desc" = "asc";
  const search = "";

  const { data: companyResponse, isLoading } = useCompanies(
    page,
    pageSize,
    sortField,
    sortOrder,
    search,
    false,
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleCompanyClick = (companyId: number) => {
    handleSelectChange(companyId);
    onCompanySelected?.();
  };

  const companies = companyResponse?.companies ?? [];
  const total = companyResponse?.total ?? 0;

  const ABOUT_TRUNCATE = 90;

  const getAboutLine = (c: (typeof companies)[0]): string => {
    const raw = (c.about && c.about.trim()) || null;
    if (raw) return raw;
    const parts = [c.industry, c.location].filter(Boolean);
    return parts.length ? parts.join(" · ") : "";
  };

  const truncate = (s: string, max: number) =>
    s.length <= max ? s : `${s.slice(0, max).trim()}…`;

  return (
    <Box sx={{ width: "100%", pt: 2, pb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 2,
          border: `1px solid ${colors.gray200}`,
          borderLeft: `4px solid ${colors.primary_dark}`,
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          sx={{
            fontWeight: 600,
            color: colors.primary_dark,
            marginBottom: 0,
          }}
        >
          {t("evaluationPage.selectCompany", {
            entity: terminology.singular,
          })}
        </Typography>
      </Paper>

      {companies.length === 0 && !isLoading && (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" sx={{ color: colors.gray700, mb: 3 }}>
            {t("evaluationPage.noCompanies", { entity: terminology.plural })}
          </Typography>
          <Button
            variant="modern"
            startIcon={<Add />}
            onClick={() => navigate("/entity/create")}
          >
            {t("evaluationPage.createButton", {
              entity: terminology.singular,
            })}
          </Button>
        </Paper>
      )}

      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress sx={{ color: colors.primary_dark }} />
        </Box>
      )}

      {!isLoading && companies.length > 0 && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              mb: 2,
            }}
          >
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={total}
              rowsPerPage={pageSize}
              page={page - 1}
              onPageChange={handleChangePage}
              sx={{
                "& .MuiTablePagination-displayedRows": {
                  backgroundColor: colors.white,
                  fontWeight: 500,
                  fontSize: "0.8125rem",
                  border: `1px solid ${colors.gray200}`,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  color: colors.primary_dark,
                  margin: 0,
                },

                "& .MuiTablePagination-toolbar": {
                  color: colors.primary_dark,
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr",
                md: "repeat(2, 1fr)",
              },
              gap: 2,
              mt: 1,
            }}
          >
            {companies.map((company) => {
              const isSelected = selectedCompanyId === company.id;
              return (
                <Paper
                  key={company.id}
                  elevation={0}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "hidden",
                    p: 2.5,
                    borderRadius: 2,
                    border: `2px solid ${
                      isSelected ? colors.accent : colors.gray200
                    }`,
                    borderLeft: `4px solid ${
                      isSelected ? colors.accent : colors.primary_dark
                    }`,
                    cursor: "pointer",
                    backgroundColor: isSelected
                      ? colors.orange50
                      : colors.white,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      borderColor: colors.accent,
                      borderLeftColor: colors.accent,
                      backgroundColor: alpha(colors.accent, 0.2),
                    },
                  }}
                  onClick={() => handleCompanyClick(company.id)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: getAboutLine(company) ? 0.75 : 0,
                        }}
                      >
                        <Box
                          sx={{
                            color: isSelected ? colors.accent : colors.gray600,
                            display: "flex",
                            alignItems: "center",
                            flexShrink: 0,
                          }}
                        >
                          {React.cloneElement(
                            getEntityIconByName(
                              entityIcons[company.icon_id - 1]?.iconName,
                            ) || <></>,
                            { sx: { fontSize: isMobile ? "1.5rem" : "2rem" } },
                          )}
                        </Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: colors.primary_dark,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {company.name}
                        </Typography>
                      </Box>
                      {getAboutLine(company) && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.gray700,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            lineHeight: 1.35,
                            pl: 0.25,
                          }}
                        >
                          {truncate(getAboutLine(company), ABOUT_TRUNCATE)}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                      {isSelected ? (
                        <CheckRounded
                          sx={{
                            fontSize: 24,
                            color: colors.accent,
                          }}
                        />
                      ) : (
                        <ArrowForwardRounded
                          sx={{
                            fontSize: 20,
                            color: colors.gray400,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};

export default SelectCompany;
